import React, { useEffect, useState } from 'react';
import Swal from 'sweetalert2';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import AdminHeader from '../../components/admin/AdminHeader';
import AdminSidebar from '../../components/admin/AdminSidebar';
import AdminFormSection from '../../components/admin/AdminFormSection';
import AdminFormField, { AdminInput, AdminTextarea, AdminSelect } from '../../components/admin/AdminFormField';
import EventImageUpload from '../../components/admin/EventImageUpload';
import PricingTierEditor from '../../components/admin/PricingTierEditor';
import SearchableCategorySelect from '../../components/admin/SearchableCategorySelect';
import { formGap, gridGap } from '../../components/admin/adminFormStyles';
import { resolveMediaUrl } from '../../utils/mediaUrl';
import { validateEventTimeRange, getEndTimeMin } from '../../utils/eventTimeValidation';
import { adminAPI, categoriesAPI, venuesAPI, uploadAPI } from '../../lib/api';
import { isAdminRole } from '../../utils/roles';

const EditEventPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user: currentUser } = useAuth();
  const isMainAdmin = isAdminRole(currentUser?.role);

  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const [categories, setCategories] = useState([]);
  const [venues, setVenues] = useState([]);
  const [organizers, setOrganizers] = useState([]);

  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState('');
  const [uploadingImage, setUploadingImage] = useState(false);
  const [usePackages, setUsePackages] = useState(false);
  const [pricingTiers, setPricingTiers] = useState([
    { id: '', name: '', description: '', price: '', maxQuantity: '' }
  ]);

  const [formData, setFormData] = useState({
    title: '',
    description: '',
    date: '',
    endDate: '',
    startTime: '',
    endTime: '',
    price: '',
    earlyBirdPrice: '',
    maxTickets: '',
    categoryId: '',
    categoryIds: [],
    venueId: '',
    imageUrl: '',
    isFeatured: false,
    status: 'ACTIVE',
    refundPolicy: '',
    organizerId: ''
  });

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        await Promise.all([
          fetchCategories(),
          fetchVenues(),
          isMainAdmin ? fetchOrganizers() : Promise.resolve()
        ]);
        await fetchEvent();
      } catch (e) {
        // noop
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const fetchCategories = async () => {
    try {
      const data = await categoriesAPI.getCategories();
      setCategories(data.data?.categories || []);
    } catch (e) {
      console.error('Failed to load categories:', e);
    }
  };

  const fetchVenues = async () => {
    try {
      // Public venues list defaults to 20; admin edit needs the full catalog
      // so the event's existing venue is always present in the dropdown.
      const data = await venuesAPI.getVenues({ limit: 500 });
      setVenues(data.data?.venues || []);
    } catch (e) {
      console.error('Failed to load venues:', e);
    }
  };

  const fetchOrganizers = async () => {
    try {
      const data = await adminAPI.getUsers({ role: 'ORGANIZER', limit: 100 });
      setOrganizers(data.data?.users || []);
    } catch (e) {
      console.error('Failed to load organizers:', e);
    }
  };

  const fetchEvent = async () => {
    try {
      const data = await adminAPI.getEvent(id);
      const e = data.data?.event || data.data;
      if (!e) throw new Error('Event not found');
      const tiers = (e.pricingTiers || []).filter((t) => t.isActive !== false);
      if (tiers.length > 0) {
        setUsePackages(true);
        setPricingTiers(tiers.map((t) => ({
          id: t.id,
          name: t.name || '',
          description: t.description || '',
          price: t.price != null ? String(t.price) : '',
          maxQuantity: t.maxQuantity != null ? String(t.maxQuantity) : ''
        })));
      } else {
        setUsePackages(false);
        setPricingTiers([{ id: '', name: '', description: '', price: '', maxQuantity: '' }]);
      }
      const venueId = e.venueId || e.venue?.id || '';
      // Keep the event's venue in the dropdown even if pagination omitted it
      if (e.venue?.id) {
        setVenues((prev) => (
          prev.some((v) => v.id === e.venue.id)
            ? prev
            : [...prev, e.venue]
        ));
      }
      setFormData({
        title: e.title || '',
        description: e.description || '',
        date: e.date ? e.date.substring(0, 10) : '',
        endDate: e.endDate ? e.endDate.substring(0, 10) : '',
        startTime: e.startTime || '',
        endTime: e.endTime || '',
        price: e.price || '',
        earlyBirdPrice: e.earlyBirdPrice || '',
        maxTickets: e.maxTickets || '',
        categoryId: e.categoryId || e.category?.id || '',
        categoryIds: (e.categories?.length
          ? e.categories.map((c) => c.id)
          : [e.categoryId || e.category?.id].filter(Boolean)),
        venueId,
        imageUrl: e.imageUrl || e.poster || '',
        isFeatured: !!e.isFeatured,
        status: e.status || 'ACTIVE',
        refundPolicy: e.refundPolicy || '',
        organizerId: e.organizerId || e.organizer?.id || ''
      });
      setImagePreview(resolveMediaUrl(e.imageUrl || e.poster || ''));
    } catch (e) {
      setError(e.message || 'Failed to load event');
    }
  };

  const handleImageChange = async (file) => {
    if (!file) return;

    const reader = new FileReader();
    reader.onloadend = () => setImagePreview(reader.result);
    reader.readAsDataURL(file);

    setImageFile(file);
    setUploadingImage(true);
    try {
      const data = await uploadAPI.uploadEventImage(file, {
        title: formData.title,
        description: formData.description
      });
      const uploadedUrl = data.data?.imageUrl;
      if (uploadedUrl) {
        setFormData((prev) => ({ ...prev, imageUrl: uploadedUrl }));
        setImagePreview(resolveMediaUrl(uploadedUrl));
        setImageFile(null);
      }
    } catch (err) {
      console.error('Image upload error:', err);
    } finally {
      setUploadingImage(false);
    }
  };

  const uploadImage = async () => {
    if (!imageFile) return formData.imageUrl;
    const data = await uploadAPI.uploadEventImage(imageFile, {
      title: formData.title,
      description: formData.description
    });
    return data.data?.imageUrl;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');

    const issues = [];
    if (!formData.title.trim()) issues.push('Event title is required.');
    if (!formData.description.trim()) issues.push('Description is required.');
    if (!formData.date) issues.push('Event date is required.');
    if (!formData.categoryIds?.length && !formData.categoryId) issues.push('At least one category is required.');
    if (!formData.venueId) issues.push('Venue is required.');
    if (isMainAdmin && !formData.organizerId) {
      issues.push('Please select an event owner (organizer).');
    }

    if (usePackages) {
      const validTiers = pricingTiers.filter((t) => t.name.trim() && t.price !== '');
      if (validTiers.length === 0) issues.push('Add at least one ticket package.');
      validTiers.forEach((tier, index) => {
        if (Number(tier.price) < 0) issues.push(`Package ${index + 1}: price must be zero or more.`);
        if (tier.maxQuantity && Number(tier.maxQuantity) < 1) {
          issues.push(`Package ${index + 1}: max quantity must be at least 1.`);
        }
      });
    } else {
      if (!formData.price && formData.price !== 0) issues.push('Regular price is required.');
      if (!formData.maxTickets) issues.push('Max tickets is required.');
    }

    const timeError = validateEventTimeRange({
      date: formData.date,
      endDate: formData.endDate,
      startTime: formData.startTime,
      endTime: formData.endTime
    });
    if (timeError) issues.push(timeError);

    if (issues.length) {
      setSaving(false);
      Swal.fire({ icon: 'warning', title: 'Please fix the form', html: issues.join('<br>') });
      return;
    }

    try {
      const newImageUrl = await uploadImage();
      const validPackages = usePackages
        ? pricingTiers
            .filter((t) => t.name.trim() && t.price !== '')
            .map((t) => ({
              ...(t.id ? { id: t.id } : {}),
              name: t.name.trim(),
              description: t.description?.trim() || '',
              price: parseFloat(t.price),
              maxQuantity: t.maxQuantity ? parseInt(t.maxQuantity, 10) : null
            }))
        : [];

      const categoryIds = (formData.categoryIds?.length
        ? formData.categoryIds
        : (formData.categoryId ? [formData.categoryId] : [])
      ).filter(Boolean);

      const payload = {
        ...formData,
        categoryIds,
        categoryId: categoryIds[0] || formData.categoryId,
        imageUrl: newImageUrl,
        price: validPackages.length
          ? Math.min(...validPackages.map((p) => p.price))
          : parseFloat(formData.price),
        maxTickets: validPackages.length
          ? validPackages.reduce((sum, p) => sum + (p.maxQuantity || 0), 0) || parseInt(formData.maxTickets, 10)
          : parseInt(formData.maxTickets, 10),
        pricingTiers: usePackages ? validPackages : []
      };

      if (payload.earlyBirdPrice) payload.earlyBirdPrice = parseFloat(payload.earlyBirdPrice);

      Object.keys(payload).forEach((k) => {
        if (payload[k] === '' || payload[k] === null) delete payload[k];
      });

      await adminAPI.updateEvent(id, payload);
      await Swal.fire({
        icon: 'success',
        title: 'Event updated successfully',
        timer: 1600,
        showConfirmButton: false
      });
      navigate('/admin/events');
    } catch (err) {
      setError(err.message || 'Failed to update event');
      Swal.fire({ icon: 'error', title: 'Update failed', text: err.message || 'Failed to update event' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex">
      <AdminSidebar isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} />
      <div className="flex-1 flex flex-col ml-0 lg:ml-72">
        <AdminHeader onMenuClick={() => setSidebarOpen(true)} />
        <main className="flex-1 p-4 lg:p-6 w-full overflow-y-auto max-w-3xl">
          <button onClick={() => navigate('/admin/events')} className="text-orange-600 hover:text-orange-700 mb-3 flex items-center text-sm">
            <i className="fas fa-arrow-left mr-2"></i>
            Back to Events
          </button>

          <div className="mb-4">
            <h1 className="text-xl font-bold text-gray-900 flex items-center gap-2">
              <span className="w-8 h-8 rounded-lg bg-orange-100 flex items-center justify-center">
                <i className="fas fa-edit text-orange-600 text-sm"></i>
              </span>
              {formData.title ? `Edit: ${formData.title}` : 'Edit Event'}
            </h1>
            <p className="text-xs text-gray-500 mt-1 ml-10">Update event details, pricing packages, and poster</p>
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded-lg mb-4 text-sm">{error}</div>
          )}

          {loading ? (
            <div className="p-10 text-center">
              <div className="animate-spin rounded-full h-10 w-10 border-4 border-orange-500 border-t-transparent mx-auto mb-3"></div>
              <p className="text-sm text-gray-600">Loading event...</p>
            </div>
          ) : (
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
              <form onSubmit={handleSubmit} className={formGap}>
                <AdminFormSection title="Basic information" description="Title and description shown on the public event page.">
                  <AdminFormField label="Event title" required>
                    <AdminInput
                      type="text"
                      value={formData.title}
                      onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                      required
                    />
                  </AdminFormField>
                  <AdminFormField label="Description" required>
                    <AdminTextarea
                      value={formData.description}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      rows={4}
                      required
                    />
                  </AdminFormField>
                </AdminFormSection>

                <AdminFormSection title="Event poster" description="JPG/PNG/WebP up to 12MB. We convert to crisp WebP, resize for the site, and name the file from your event title.">
                  <EventImageUpload
                    inputId="edit-event-image"
                    imagePreview={imagePreview}
                    onFileSelect={handleImageChange}
                    onRemove={() => {
                      setImageFile(null);
                      setImagePreview('');
                      setFormData({ ...formData, imageUrl: '' });
                    }}
                  />
                  {uploadingImage && (
                    <p className="text-xs text-orange-600 flex items-center gap-1.5 mt-1">
                      <i className="fas fa-spinner fa-spin" />
                      Uploading image...
                    </p>
                  )}
                </AdminFormSection>

                <AdminFormSection title="Schedule" description="When the event starts and ends.">
                  <div className={`grid grid-cols-1 sm:grid-cols-2 ${gridGap}`}>
                    <AdminFormField label="Start date" required>
                      <AdminInput type="date" value={formData.date} onChange={(e) => setFormData({ ...formData, date: e.target.value })} required />
                    </AdminFormField>
                    <AdminFormField label="End date">
                      <AdminInput
                        type="date"
                        value={formData.endDate}
                        min={formData.date || undefined}
                        onChange={(e) => setFormData({ ...formData, endDate: e.target.value })}
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        For next-day events, set an end date so the end time can be earlier than the start time.
                      </p>
                    </AdminFormField>
                    <AdminFormField label="Start time">
                      <AdminInput type="time" value={formData.startTime} onChange={(e) => setFormData({ ...formData, startTime: e.target.value })} />
                    </AdminFormField>
                    <AdminFormField label="End time">
                      <AdminInput
                        type="time"
                        value={formData.endTime}
                        min={getEndTimeMin({
                          date: formData.date,
                          endDate: formData.endDate,
                          startTime: formData.startTime
                        })}
                        onChange={(e) => setFormData({ ...formData, endTime: e.target.value })}
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        Optional. Leave blank for “till late” (no fixed end time).
                      </p>
                    </AdminFormField>
                  </div>
                </AdminFormSection>

                <AdminFormSection
                  title="Ticket pricing"
                  description="Single price or multiple packages (Adult, Kids, Group, etc.)."
                >
                  <label className="flex items-center gap-2 cursor-pointer py-1">
                    <input
                      type="checkbox"
                      checked={usePackages}
                      onChange={(e) => setUsePackages(e.target.checked)}
                      className="rounded border-gray-300 text-orange-600 focus:ring-orange-500"
                    />
                    <span className="text-sm text-gray-800">Multiple ticket packages</span>
                  </label>

                  {usePackages ? (
                    <PricingTierEditor tiers={pricingTiers} onChange={setPricingTiers} />
                  ) : (
                    <div className={`grid grid-cols-1 sm:grid-cols-3 ${gridGap}`}>
                      <AdminFormField label="Regular price (KES)" required>
                        <AdminInput type="number" step="0.01" value={formData.price} onChange={(e) => setFormData({ ...formData, price: e.target.value })} required={!usePackages} />
                      </AdminFormField>
                      <AdminFormField label="Early bird (KES)">
                        <AdminInput type="number" step="0.01" value={formData.earlyBirdPrice} onChange={(e) => setFormData({ ...formData, earlyBirdPrice: e.target.value })} />
                      </AdminFormField>
                      <AdminFormField label="Max tickets" required>
                        <AdminInput type="number" value={formData.maxTickets} onChange={(e) => setFormData({ ...formData, maxTickets: e.target.value })} required={!usePackages} />
                      </AdminFormField>
                    </div>
                  )}
                </AdminFormSection>

                <AdminFormSection title="Location & category">
                  <div className={`grid grid-cols-1 sm:grid-cols-2 ${gridGap}`}>
                    <div className="sm:col-span-2">
                      <SearchableCategorySelect
                        categories={categories}
                        value={formData.categoryIds?.length ? formData.categoryIds : (formData.categoryId ? [formData.categoryId] : [])}
                        onChange={(categoryIds) => setFormData({
                          ...formData,
                          categoryIds,
                          categoryId: categoryIds[0] || ''
                        })}
                        required
                        multiple
                      />
                    </div>
                    <AdminFormField label="Venue" required>
                      <AdminSelect value={formData.venueId} onChange={(e) => setFormData({ ...formData, venueId: e.target.value })} required>
                        <option value="">Select venue</option>
                        {venues.map((v) => (
                          <option key={v.id} value={v.id}>
                            {v.name}{v.city ? `, ${v.city}` : ''}
                          </option>
                        ))}
                      </AdminSelect>
                    </AdminFormField>
                  </div>
                </AdminFormSection>

                {isMainAdmin ? (
                  <AdminFormSection
                    title="Event owner"
                    description="Assign which organizer account can manage sales and tickets for this event."
                  >
                    <AdminFormField label="Organizer account" required>
                      <AdminSelect
                        value={formData.organizerId}
                        onChange={(e) => setFormData({ ...formData, organizerId: e.target.value })}
                        required
                      >
                        <option value="">Select organizer…</option>
                        {organizers.map((org) => (
                          <option key={org.id} value={org.id}>
                            {org.name} ({org.email})
                          </option>
                        ))}
                      </AdminSelect>
                    </AdminFormField>
                    {organizers.length === 0 ? (
                      <p className="text-xs text-amber-700">
                        No organizer accounts yet. Create one under Users → Create account (role: Organizer).
                      </p>
                    ) : null}
                  </AdminFormSection>
                ) : null}

                <AdminFormSection title="Policies & visibility">
                  <AdminFormField label="Refund policy">
                    <AdminTextarea value={formData.refundPolicy} onChange={(e) => setFormData({ ...formData, refundPolicy: e.target.value })} rows={2} />
                  </AdminFormField>
                  <div className={`grid grid-cols-1 sm:grid-cols-2 ${gridGap} items-center`}>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input type="checkbox" checked={formData.isFeatured} onChange={(e) => setFormData({ ...formData, isFeatured: e.target.checked })} className="rounded border-gray-300 text-orange-600 focus:ring-orange-500" />
                      <span className="text-sm text-gray-700">Feature on homepage</span>
                    </label>
                    <AdminFormField label="Status">
                      <AdminSelect value={formData.status} onChange={(e) => setFormData({ ...formData, status: e.target.value })}>
                        <option value="ACTIVE">Active</option>
                        <option value="SOLD_OUT">Sold out</option>
                        <option value="CANCELLED">Cancelled</option>
                        <option value="COMPLETED">Completed</option>
                      </AdminSelect>
                    </AdminFormField>
                  </div>
                </AdminFormSection>

                <div className="flex gap-3 pt-3 border-t border-gray-100">
                  <button type="button" onClick={() => navigate('/admin/events')} className="flex-1 px-4 py-2.5 border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-gray-50 font-medium">
                    Cancel
                  </button>
                  <button type="submit" disabled={saving || uploadingImage} className="flex-1 px-4 py-2.5 bg-orange-600 text-white rounded-lg hover:bg-orange-700 text-sm font-medium disabled:opacity-50">
                    {saving ? 'Saving...' : uploadingImage ? 'Uploading...' : 'Save changes'}
                  </button>
                </div>
              </form>
            </div>
          )}
        </main>
      </div>
    </div>
  );
};

export default EditEventPage;
